import { useCallback, useEffect, useState } from 'react';
import { apiFetch, apiRoutes } from '@/lib/api';

interface TimeUntilReset {
  hours: number;
  minutes: number;
  seconds: number;
}

interface DownloadPermission {
  canDownload: boolean;
  alreadyDownloaded: boolean;
  reason?: string;
  limitReached?: boolean;
  timeUntilReset?: TimeUntilReset;
  redirectTo?: string;
}

export function useDownloadCheck() {
  const [checking, setChecking] = useState(false);
  const [timeUntilReset, setTimeUntilReset] = useState<TimeUntilReset | null>(null);

  /**
   * Check if user can download an artwork
   */
  const checkPermission = useCallback(async (artworkId: string, isPremium: boolean): Promise<DownloadPermission> => {
    setChecking(true);
    try {
      const response = await apiFetch<DownloadPermission>(
        `${apiRoutes.downloads.check(artworkId)}?isPremium=${isPremium}`,
        { method: 'GET' }
      );
      return response;
    } catch (error: any) {
      // If it's a 403, return the limit reached info
      if (error.status === 403 && error.data) {
        return error.data;
      }
      throw error;
    } finally {
      setChecking(false);
    }
  }, []);

  /**
   * Record a download
   */
  const recordDownload = useCallback(async (artworkId: string, isPremium: boolean) => {
    try {
      await apiFetch(apiRoutes.downloads.record(), {
        method: 'POST',
        body: JSON.stringify({ artworkId, isPremium })
      });
    } catch (error) {
      console.error('Failed to record download:', error);
      // Don't throw - recording is not critical
    }
  }, []);

  /**
   * Get time until next reset
   */
  const fetchTimeUntilReset = useCallback(async () => {
    try {
      const response = await apiFetch<{ timeUntilReset: TimeUntilReset }>(
        apiRoutes.downloads.timeUntilReset(),
        { method: 'GET' }
      );
      setTimeUntilReset(response.timeUntilReset);
      return response.timeUntilReset;
    } catch (error) {
      console.error('Failed to fetch time until reset:', error);
      return null;
    }
  }, []);

  // Auto-fetch time until reset when component mounts
  useEffect(() => {
    void fetchTimeUntilReset();
    
    // Refresh every minute
    const interval = setInterval(() => {
      void fetchTimeUntilReset();
    }, 60000);

    return () => clearInterval(interval);
  }, [fetchTimeUntilReset]);

  return {
    checking,
    checkPermission,
    recordDownload,
    timeUntilReset,
    fetchTimeUntilReset
  };
}
